import { withAuthenticationRequired } from '@auth0/auth0-react'
import { logEvent } from '../../../analytics/analytics'
import { Alert, Box, Chip, CircularProgress, Grid, Typography, keyframes } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'

import { Helmet } from 'react-helmet'
import { getLocation } from '../../../helpers/location-helper'
import {
  ONBOARD_PATH_PARAMS,
  OnboardPage,
  getSteps,
  onboardContentNicheStepList,
  onboardStepTypes as onboardEaterStepTypes,
  onboardInfluencerStep,
} from '../../../helpers/onboard-helper'
import { getStepPoints, getOnboardingPointsSoFar, ONBOARD_STEP_POINTS } from '../../../helpers/onboarding-points'
import { ROUTES } from '../../../helpers/routes-helper'
import {
  PENDING_REFERRAL_CONTEXT_KEY,
  RESTAURANT_SIGNUP_REFERRAL_CONTEXT,
} from '../../../helpers/static-constants'
import useAppUtilities from '../../../hooks/useAppUtilities'
import appTheme from '../../../theme'
import { useAuthHelper } from '../../../hooks/useAuthHelper'
import { useAppSelector } from '../../../store'
import {
  loginAudit,
  restaurantOnboardRequest,
  selectLogin,
  setLocation,
  updateRestaurantOnboard,
} from '../../../store/authSlice'
import {
  fetchEaterProfile,
  selectIsLoading,
  selectIsNewRestaurant,
  selectMessage,
  selectOnboardPayload,
  selectPersonalizePayload,
  selectRestaurant,
  selectRole,
  selectSeverity,
  selectShouldOpen,
  setDiningFrequency,
  setOnboardPayload,
  setOpen,
  setPersonalizePayload,
  setRole,
  setSocialLinks,
  onboardStepTypes as onboardStep,
} from '../../../store/onboardSlice'
import { setEater, setMessage, setSeverity } from '../../../store/profileSlice'
import {
  Eater,
  EaterPayload,
  GeneralData,
  IStepper,
  Login,
  OnboardDiningFrequencyRefMethods,
  OnboardPersonalizeMethods,
  OnboardRequestMethods,
  OnboardRequestPayloadType,
  OnboardRestaurantMethods,
  OnboardSelectionMethods,
  OnboardSocialRefMethods,
  PersonalFavoriteMethods,
  StepsPayloadMap,
} from '../../../types'
import useRudderStackAnalytics from '../../../useRudderAnalytics'
import { debugLog } from '../../../utils/log-helper'
import { MuiAlert, MuiBackdrop, MuiSnackbar } from '../styled/global.styled'
import OnboardChooseRole from './OnboardChooseRole'
import OnboardDiningFrequency from './OnboardDiningFrequency'
import OnboardLocateYourRestaurant from './OnboardLocateYourRestaurant'
import OnboardPersonalFavorites from './OnboardPersonalFavorites'
import OnboardPersonalize from './OnboardPersonalize'
import OnboardPreferences from './OnboardPreferences'
import OnboardRequest from './OnboardRequest'
import OnboardSocial from './OnboardSocial'
import OnboardStepper from './OnboardSteppar'
import {
  OnboardContent,
  OnboardContentContainer,
  OnboardContentWarper,
  OnboardHeaderBackButton,
  OnboardHeaderButtonWarper,
  OnboardHeaderNextButton,
  OnboardHeaderSkipButton,
  OnboardHeaderWarper,
  OnboardMainContainer,
  OnboardSidebarButtonImg,
  OnboardSidebarButtonTitle,
  OnboardSidebarContainer,
  OnboardSidebarLogoImg,
  OnboardSidebarLogoutButton,
  OnboardSidebarTitleTypography,
  OnboardSidebarWarper,
  SideTitleAndStepper,
} from './styled/OnboardContainer.styled'
import { useNavigate } from 'react-router'
import DescribeYourselfStep from '../../influencers/onboard/components/DescribeYourselfStep'
import {
  OnboardContentNicheMethods,
  OnboardDescribeYourselfMethods,
  OnboardSocialChannelMethods,
} from '../../../types/contentCreatorTypes'
import OnboardInfluencer from './OnboardInfluencer'
import SocialChannelsStep from '../../influencers/onboard/components/SocialChannelsStep'
import ContentNicheStep from '../../influencers/onboard/components/ContentNicheStep'

// Animation keyframes
const pulseAnimation = keyframes`
  0% {
    background-color: transparent;
  }
  50% {
    background-color: ${appTheme.palette.success[100]};
  }
  100% {
    background-color: transparent;
  }
`

const fadeOutAnimation = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.8);
  }
`

const Onboard: React.FC = (): JSX.Element => {
  const { theme, dispatch, isSmallScreen } = useAppUtilities()
  const navigate = useNavigate()
  const { logoutAuthUser } = useAuthHelper()
  const [analytics, isAnalyticsReady] = useRudderStackAnalytics()
  const role = useAppSelector(selectRole)
  const message = useAppSelector(selectMessage)
  const severity = useAppSelector(selectSeverity)
  const loginResponse = useAppSelector(selectLogin)
  const isLoading = useAppSelector(selectIsLoading)
  const restaurant = useAppSelector(selectRestaurant)
  const openServer = useAppSelector(selectShouldOpen)
  const isNewRestaurant = useAppSelector(selectIsNewRestaurant)
  const onboardPayload = useAppSelector(selectOnboardPayload)
  const personalizePayload = useAppSelector(selectPersonalizePayload)
  const onboardStepTypes = useAppSelector(onboardStep)
  const stepTypesToUse = role === 'influencer' ? onboardInfluencerStep : onboardEaterStepTypes
  const defaultSteps = getSteps(stepTypesToUse, role)
  const [selectedRole, setSelectedRole] = useState(role)
  const [pageTitle, setPageTitle] = useState<string>('')
  const [steps, setSteps] = useState<IStepper[]>(defaultSteps)
  const [currentStep, setCurrentStep] = useState<IStepper>(defaultSteps[0])
  const [activeStep, setActiveStep] = useState(onboardStepTypes.CHOOSE_ROLE)
  const [isNextButtonDisable, setNextButtonDisable] = useState<boolean>(false)
  const [isLoadingRestaurants, setLoadingRestaurants] = useState<boolean>(false)
  const [personalizeData, setPersonalizeData] =
    useState<GeneralData>(personalizePayload)
  const [stepsPayloads, setStepsPayloads] =
    useState<StepsPayloadMap>(onboardPayload)
  // Points tracking state
  const [completedRewardedSteps, setCompletedRewardedSteps] = useState<number>(0)
  const [lastEarnedPoints, setLastEarnedPoints] = useState<number | null>(null)
  const [showEarnedAnimation, setShowEarnedAnimation] = useState<boolean>(false)
  const itemListRef = useRef<OnboardSelectionMethods>(null)
  const restaurantRef = useRef<OnboardRestaurantMethods>(null)
  const onboardSocialRef = useRef<OnboardSocialRefMethods>(null)
  const onboardingRequestRef = useRef<OnboardRequestMethods>(null)
  const personalFavoriteRef = useRef<PersonalFavoriteMethods>(null)
  const onboardPersonalizeRef = useRef<OnboardPersonalizeMethods>(null)
  const describeYourselfRef = useRef<OnboardDescribeYourselfMethods>(null)
  const socialChannelsRef = useRef<OnboardSocialChannelMethods>(null)
  const contentNicheRef = useRef<OnboardContentNicheMethods>(null)
  const onboardDiningFrequencyRef =
    useRef<OnboardDiningFrequencyRefMethods>(null)

  useEffect(() => {
    dispatch(setOnboardPayload(stepsPayloads))
  }, [stepsPayloads, dispatch])

  useEffect(() => {
    dispatch(setPersonalizePayload(personalizeData))
  }, [personalizeData, dispatch])

  // Calculate completed rewarded steps from activeStep
  useEffect(() => {
    let completedCount = 0
    steps.forEach((step) => {
      if (step.step < activeStep && step.page) {
        const points = getStepPoints(step.page)
        if (points !== null) {
          completedCount++
        }
      }
    })
    setCompletedRewardedSteps(completedCount)
  }, [activeStep, steps])

  useEffect(() => {
    const stepTypesToUse = selectedRole === 'influencer' ? onboardInfluencerStep : onboardEaterStepTypes
    setSteps(getSteps(stepTypesToUse, selectedRole))
    dispatch(setRole(selectedRole))
  }, [selectedRole, dispatch])

  useEffect(() => {
    setPageTitle(`Resactly | ${currentStep.title} - Onboarding`)
  }, [currentStep])

  // Set Next button disabled by default when step changes (except CHOOSE_ROLE)
  useEffect(() => {
    if (activeStep === onboardStepTypes.CHOOSE_ROLE) {
      // CHOOSE_ROLE handles its own validation
      setNextButtonDisable(!selectedRole || !selectedRole.length)
    } else {
      // For all other steps, disable Next by default until component reports valid
      setNextButtonDisable(true)
    }
  }, [activeStep, selectedRole, onboardStepTypes.CHOOSE_ROLE])

  useEffect(() => {
    if (currentStep?.path) {
      navigate(`${ROUTES.ONBOARD}/${currentStep.path}`)
    }

    if (analytics && isAnalyticsReady) {
      analytics?.page('PAGE_OPENED', {
        path: ROUTES.RESTAURANTS,
        title: pageTitle,
        url: `${window.location.origin}${ROUTES.ONBOARD}/${currentStep.path}`,
        category: 'Onboard',
        name: `${currentStep.title} Viewed`,
        startTime: new Date().toISOString(),
      })
    }
  }, [pageTitle, currentStep, analytics, isAnalyticsReady])

  useEffect(() => {
    return () => {
      analytics?.page('PAGE_CLOSED', {
        path: ROUTES.RESTAURANTS,
        title: pageTitle,
        url: `${window.location.origin}${ROUTES.ONBOARD}/${currentStep.path}`,
        category: 'Onboard',
        name: `${currentStep.title} Viewed`,
        endTime: new Date().toISOString(),
      })
    }
  }, [currentStep, location])

  useEffect(() => {
    // Use selectLogin which handles migration, or fallback to migrated localStorage
    const localStorageData = window.localStorage.getItem('login')
    const localLoginData: Login = localStorageData
      ? JSON.parse(localStorageData)
      : {}

    const eaterIsDone =
      loginResponse?.eaterDTO?.step === 'DONE' ||
      localLoginData.eaterDTO?.step === 'DONE'

    // Restaurant-referral invitee: skip the role picker and drop them straight into restaurant
    // onboarding (they already declared intent by accepting a restaurant invite). Preset the role to
    // 'restaurant' and jump to LOCATE_YOUR_RESTAURANT — the first restaurant-info step. Without this,
    // the eater-step restore below would forward an existing (non-NEW) eater into eater onboarding.
    // Only defer once they've finished onboarding as an eater (DONE), which the block below sends
    // HOME.
    // Read the CONTEXT only — the referral CODE is cleared by BaseLayout's claim the moment the
    // referral is confirmed, but the restaurant context is deliberately kept until onboarding
    // completes (see BaseLayout claimPendingReferral + the completion handler below). Requiring the
    // code here dropped the invitee into eater onboarding when the claim won the race.
    const restaurantReferralPending =
      localStorage.getItem(PENDING_REFERRAL_CONTEXT_KEY) ===
      RESTAURANT_SIGNUP_REFERRAL_CONTEXT
    if (restaurantReferralPending && !eaterIsDone) {
      dispatch(setRole('restaurant'))
      setSelectedRole('restaurant')
      const restaurantSteps = getSteps(onboardEaterStepTypes, 'restaurant')
      setSteps(restaurantSteps)
      const locateStep = restaurantSteps.find(
        (s) => s.page === 'LOCATE_YOUR_RESTAURANT',
      )
      if (locateStep) {
        setActiveStep(locateStep.step)
      }
      navigate(`${ROUTES.ONBOARD}/${ONBOARD_PATH_PARAMS.LOCATE_YOUR_RESTAURANT}`)
      return
    }

    // Note: step field is always present in lite format, so this check works
    if (eaterIsDone) {
      void getLocation().then((location) => {
        dispatch(setLocation(location))
        navigate(ROUTES.HOME)
      })
    } else {
      // Funnel entry: the user is on the onboarding flow and not already DONE.
      // Record the funnel start once so completion can report a real duration_ms.
      // Persisted in sessionStorage so it survives reloads within the flow.
      if (!sessionStorage.getItem('onboarding_start_ts')) {
        sessionStorage.setItem('onboarding_start_ts', String(Date.now()))
      }
      logEvent('onboarding_started', { screen_name: 'onboard' })
      // A brand-new signup that BaseLayout routed to the role picker has NOT chosen a role yet.
      // login-lite provisions them as an ACTIVE EATER at PERSONALIZE (the "John Smith" fix), which
      // would otherwise trip the "resume eater onboarding" branch below and skip the picker —
      // hiding the restaurant path from restaurant owners. Honor the role-picker route: render it
      // and let them choose. A returning eater mid-flow is routed by BaseLayout to their actual
      // step (never choose-your-role), so this guard doesn't disturb resume.
      const onRolePickerRoute = window.location.pathname.endsWith(
        `/${ONBOARD_PATH_PARAMS.CHOOSE_ROLE}`,
      )
      if (onRolePickerRoute) {
        setSteps(defaultSteps)
        return
      }
      if (
        (loginResponse?.type === 'EATER' &&
          loginResponse?.eaterDTO?.step !== 'NEW' &&
          loginResponse?.status !== 'NEW') ||
        (localLoginData.type === 'EATER' &&
          localLoginData.eaterDTO?.step !== 'NEW' &&
          loginResponse?.status !== 'NEW')
      ) {
        setSelectedRole('eater')

        const onboardList: Eater | undefined =
          loginResponse?.eaterDTO ?? localLoginData.eaterDTO

        const routeName = getSteps(onboardEaterStepTypes, 'eater').find(
          (step) => step.page === onboardList?.step,
        )

        onUpdatePersonalInfoStep(onboardList)
        onUpdateLikeDislikeStep(onboardList, 'eater')
        onUpdateDiningFrequencyStep(onboardList)
        onUpdateSocialLinkStep(onboardList)

        if (routeName) {
          onUpdateRouteAndStep(routeName)
        }
      } else if (
        (loginResponse?.type === 'INFLUENCER' &&
          loginResponse?.eaterDTO?.step !== 'NEW' &&
          loginResponse?.status !== 'NEW') ||
        (localLoginData.type === 'INFLUENCER' &&
          localLoginData.eaterDTO?.step !== 'NEW' &&
          loginResponse?.status !== 'NEW')
      ) {
        setSelectedRole('influencer')
        const onboardList: Eater | undefined =
          loginResponse?.eaterDTO ?? localLoginData.eaterDTO

        const routeName = getSteps(onboardInfluencerStep, 'influencer').find(
          (step) => step.page === onboardList?.step,
        )

        onUpdatePersonalInfoStep(onboardList)
        onUpdateLikeDislikeStep(onboardList, 'influencer')
        onUpdateDiningFrequencyStep(onboardList)

        if (routeName) {
          onUpdateRouteAndStep(routeName)
        }
      } else {
        setSteps(defaultSteps)
        if (currentStep?.path) {
          navigate(`${ROUTES.ONBOARD}/${currentStep.path}`)
        }
      }
    }
  }, [])

  useEffect(() => {
    const step = steps.find((item) => item.step === activeStep)
    if (step) {
      setCurrentStep(step)
    }
  }, [activeStep, steps])

  const onUpdateSocialLinkStep = (onboardList: Eater | undefined): void => {
    const socialLinksData = {
      facebook: onboardList?.facebook ?? '',
      instagram: onboardList?.instagram ?? '',
      tiktok: onboardList?.tiktok ?? '',
      x: onboardList?.x ?? '',
    }

    dispatch(setSocialLinks(socialLinksData))
  }
  const onUpdateDiningFrequencyStep = (
    onboardList: Eater | undefined,
  ): void => {
    const words = onboardList?.diningFrequency?.split(' ')
    if (words && words.length) {
      dispatch(
        setDiningFrequency({
          number: Number(words[0]),
          frequency: words[words.length - 1].toUpperCase(),
        }),
      )
    }
  }
  const onUpdateLikeDislikeStep = (
    onboardList: Eater | undefined,
    type: 'eater' | 'influencer' = 'eater',
  ): void => {
    const steps = [
      {
        step: type === 'eater' ? 3 : 6,
        likeKey: 'ingredientLikes',
        dislikeKey: 'ingredientDislikes',
      },
      {
        step: type === 'eater' ? 4 : 7,
        likeKey: 'cuisineLikes',
        dislikeKey: 'cuisineDislikes',
      },
      {
        step: type === 'eater' ? 5 : 8,
        likeKey: 'foodPreparationLiked',
        dislikeKey: 'foodPreparationDisliked',
      },
      {
        step: type === 'eater' ? 6 : 9,
        likeKey: 'ambienceLiked',
        dislikeKey: 'ambienceDisliked',
      },
      {
        step: type === 'eater' ? 7 : 10,
        likeKey: 'dietaryPreferences',
        dislikeKey: null,
      },
      {
        step: type === 'eater' ? 8 : 11,
        likeKey: 'allergies',
        dislikeKey: null,
      },
      {
        step: type === 'eater' ? 10 : 13,
        likeKey: 'favoriteRestaurants',
        dislikeKey: 'favoriteReviewers',
      },
    ]

    const data = steps.reduce((acc, { step, likeKey, dislikeKey }) => {
      acc[step] = {
        currentStep: step,
        nextStep: step + 1,
        ...(step !== (type === 'eater' ? 10 : 13) && {
          likedItems:
            onboardList && onboardList[likeKey]
              ? onboardList[likeKey].split(',')
              : [],
        }),
        ...(step !== (type === 'eater' ? 10 : 13) && {
          dislikedItems:
            dislikeKey && onboardList && onboardList[dislikeKey]
              ? onboardList[dislikeKey].split(',')
              : [],
        }),
        ...(step === (type === 'eater' ? 10 : 13) && {
          favoritesRestaurantItems:
            onboardList && onboardList[likeKey]
              ? onboardList[likeKey].split(',')
              : [],
        }),
        ...(step === (type === 'eater' ? 10 : 13) && {
          foodReviewerItems:
            dislikeKey && onboardList && onboardList[dislikeKey]
              ? onboardList[dislikeKey].split(',')
              : [],
        }),
      }
      return acc
    }, {})
    setStepsPayloads(data)
  }
  const onUpdatePersonalInfoStep = (onboardList: Eater | undefined): void => {
    const personalize = {
      firstName: onboardList?.firstName,
      lastName: onboardList?.lastName,
      dob: onboardList?.dob,
      phone: onboardList?.phone,
      addressLine1: onboardList?.addressLine1,
      addressLine2: onboardList?.addressLine2,
      city: onboardList?.city,
      state: onboardList?.country,
      zipcode: onboardList?.zipcode,
      country: onboardList?.country,
      incomeLevel: Number(onboardList?.incomeLevel),
      gender: onboardList?.gender,
      occupation: onboardList?.occupation,
    }

    setPersonalizeData(personalize)
  }
  const onUpdateRouteAndStep = (routeName: IStepper): void => {
    setCurrentStep(routeName)
    setActiveStep(routeName.step)
    // setStepsPayloads(data)
    navigate(`${ROUTES.ONBOARD}/${routeName.path}`)
  }

  const handleSkip = async (): Promise<void> => {
    // Special handling for Allergies step: save empty array instead of skipping
    if (currentStep.step === onboardStepTypes.ALLERGIES) {
      const payload: EaterPayload = {
        page: OnboardPage[currentStep.page],
        value: [],
      }
      await handleFetchProfile(payload)
      return
    }
    
    // For all other steps, use the original skip behavior
    await proceedToNextStep()
  }

  const proceedToNextStep = async (): Promise<void> => {
    if (currentStep.step === onboardStepTypes.CHOOSE_ROLE) {
      if (currentStep?.nextStep) {
        setActiveStep(currentStep.nextStep)
      }
    } else {
      const payload: EaterPayload = {
        page: 'SKIP',
      }

      const resultAction = await dispatch(fetchEaterProfile(payload))
      if (fetchEaterProfile.fulfilled.match(resultAction)) {
        await fetchLoginUser()
      }
    }
  }

  const prevStep = (): void => {
    const newStep = currentStep?.prevStep

    if (newStep) {
      setActiveStep(newStep)
    } else {
      navigate('/')
    }
  }

  const handleFetchProfile = async (payload: EaterPayload): Promise<void> => {
    const resultAction = await dispatch(fetchEaterProfile(payload))

    if (fetchEaterProfile.fulfilled.match(resultAction)) {
      dispatch(setEater(resultAction.payload.eaterDTO))

      // Each successful step save advances the onboarding funnel.
      logEvent('onboarding_step_completed', {
        step: String(currentStep.page),
        screen_name: 'onboard',
      })

      // Check if this was a rewarded step completion
      const points = getStepPoints(payload.page)
      if (points !== null) {
        // The "+N points" award is a visible earn event — emit points_earned so the loyalty loop
        // captures where points are granted (previously only onboarding_step_completed fired here).
        logEvent('points_earned', {
          points_amount: points,
          reason: 'onboarding_step',
          screen_name: 'onboard',
        })
        // Update animation state
        setLastEarnedPoints(points)
        setShowEarnedAnimation(true)
        setTimeout(() => {
          setShowEarnedAnimation(false)
        }, 1200)
        
        // Update toast message to include points
        dispatch(setMessage(`Saved • +${points} points`))
        dispatch(setSeverity('success'))
        dispatch(setOpen(true))
      } else {
        // Non-rewarded step, just show saved message
        dispatch(setMessage('Saved'))
        dispatch(setSeverity('success'))
        dispatch(setOpen(true))
      }
      
      if (currentStep?.nextStep) {
        setActiveStep(currentStep.nextStep)
      } else if (
        currentStep.step === onboardStepTypes.SOCIAL &&
        role === 'eater'
      ) {
        await fetchLoginUser()
      } else if (
        currentStep.step === onboardStepTypes.PERSONAL_FAVORITES &&
        loginResponse?.type === 'INFLUENCER'
      ) {
        await fetchLoginUser()
      } else if (
        currentStep.step === onboardStepTypes.REQUEST_ONBOARDING_RESTAURANT
      ) {
        // Restaurant onboarding complete (pending review) — the referral intent is fulfilled, so
        // clear the kept restaurant-referral context to stop future logins re-routing here.
        localStorage.removeItem(PENDING_REFERRAL_CONTEXT_KEY)
        navigate(`${ROUTES.RESTAURANT_ADMIN_PENDING}`)
      }
    }
    if (fetchEaterProfile.rejected.match(resultAction)) {
      onNextStepError(payload)
      if (currentStep?.nextStep && role === 'influencer') {
        setActiveStep(currentStep.nextStep)
      }
    }
  }

  const fetchLoginUser = async (): Promise<void> => {
    await getLocation().then(async (location) => {
      dispatch(setLocation(location))
      await dispatch(loginAudit())
      // Fire signup_completed attributed to the originating funnel if set
      const signupSource = sessionStorage.getItem('signup_source')
      // Elapsed time since funnel entry (onboarding_started), 0 if start was never recorded.
      const startTs = Number(sessionStorage.getItem('onboarding_start_ts'))
      const durationMs = startTs > 0 ? Math.max(0, Date.now() - startTs) : 0
      logEvent('signup_completed', {
        duration_ms: durationMs,
        screen_name: 'onboard',
        ...(signupSource === 'menu_scan' ? { source: 'menu_scan' as const } : {}),
      })
      // Onboarding funnel exit — pairs with onboarding_started/step_completed.
      logEvent('onboarding_completed', {
        duration_ms: durationMs,
        screen_name: 'onboard',
      })
      if (signupSource) sessionStorage.removeItem('signup_source')
      sessionStorage.removeItem('onboarding_start_ts')
      navigate('/')
    })
  }

  const onNextStepError = (payload: EaterPayload): void => {
    const localStorageData: Login = JSON.parse(
      window.localStorage.getItem('login') ?? '{}',
    )
    const { eaterDTO } = localStorageData

    const updateLocalStorage = (updatedData: Partial<Eater>): void => {
      localStorage.setItem(
        'login',
        JSON.stringify({
          ...localStorageData,
          eaterDTO: {
            ...eaterDTO,
            ...updatedData,
          },
        }),
      )
    }

    const joinPayloadValues = (values: string[] | undefined): string =>
      values?.join(',') ?? ''

    let frequency: string = ''
    let number: number = 0

    switch (currentStep.step) {
      case onboardStepTypes.PERSONALIZE:
        updateLocalStorage(payload.eaterDTO)
        break
      case onboardStepTypes.INGREDIENTS:
        updateLocalStorage({
          ingredientLikes: joinPayloadValues(payload.likes),
          ingredientDislikes: joinPayloadValues(payload.dislikes),
        })
        break
      case onboardStepTypes.CUISINES:
        updateLocalStorage({
          cuisineLikes: joinPayloadValues(payload.likes),
          cuisineDislikes: joinPayloadValues(payload.dislikes),
        })
        break
      case onboardStepTypes.FOOD_PREPARATIONS:
        updateLocalStorage({
          foodPreparationLiked: joinPayloadValues(payload.likes),
          foodPreparationDisliked: joinPayloadValues(payload.dislikes),
        })
        break
      case onboardStepTypes.DINING_AMBIENCE:
        updateLocalStorage({
          ambienceLiked: joinPayloadValues(payload.likes),
          ambienceDisliked: joinPayloadValues(payload.dislikes),
        })
        break
      case onboardStepTypes.DIETARY_PREFERENCE_RESTRICTION:
        updateLocalStorage({
          dietaryPreferences: joinPayloadValues(payload.value),
        })
        break
      case onboardStepTypes.ALLERGIES:
        updateLocalStorage({
          allergies: joinPayloadValues(payload.value),
        })
        break
      case onboardStepTypes.DINING_FREQUENCY:
        frequency = payload?.diningFrequency?.frequency ?? ''
        number = payload?.diningFrequency?.number ?? 0
        updateLocalStorage({
          diningFrequency: `${number} times per ${frequency}`,
        })
        break
      case onboardStepTypes.PERSONAL_FAVORITES:
        updateLocalStorage({
          favoriteRestaurants: joinPayloadValues(
            payload?.favorites?.restaurants,
          ),
          favoriteReviewers: joinPayloadValues(payload?.favorites?.reviewers),
        })
        break
      case onboardStepTypes.SOCIAL:
        updateLocalStorage({
          facebook: payload?.socialValue?.facebook,
          instagram: payload?.socialValue?.instagram,
          tiktok: payload?.socialValue?.tiktok,
          x: payload?.socialValue?.x,
        })
        break
      default:
        break
    }
  }

  const handleNext = async (): Promise<void> => {
    if (
      currentStep.step !== onboardStepTypes.REQUEST_ONBOARDING_RESTAURANT &&
      currentStep.step !== onboardStepTypes.LOCATE_YOUR_RESTAURANT
    ) {
      const payload: EaterPayload = {
        page: OnboardPage[currentStep.page],
      }

      switch (currentStep.step) {
        case onboardStepTypes.PERSONALIZE:
          if (onboardPersonalizeRef.current) {
            payload.eaterDTO = await onboardPersonalizeRef.current.getPayload()
          }
          break
        case onboardStepTypes.INGREDIENTS:
        case onboardStepTypes.CUISINES:
        case onboardStepTypes.FOOD_PREPARATIONS:
        case onboardStepTypes.DINING_AMBIENCE:
        case onboardStepTypes.DIETARY_PREFERENCE_RESTRICTION:
        case onboardStepTypes.ALLERGIES:
          if (itemListRef.current) {
            const { likedItems, dislikedItems } =
              itemListRef.current.getPayload()

            const updatedPayload = {
              currentStep: currentStep.step,
              nextStep: currentStep?.nextStep as number,
              likedItems,
              dislikedItems,
            }

            setStepsPayloads((prevPayloads) => ({
              ...prevPayloads,
              [currentStep.step]: updatedPayload,
            }))

            switch (currentStep.step) {
              case onboardStepTypes.DIETARY_PREFERENCE_RESTRICTION:
              case onboardStepTypes.ALLERGIES:
                payload.value = likedItems
                break
              default:
                payload.likes = likedItems
                payload.dislikes = dislikedItems
                break
            }
          }
          break
        case onboardStepTypes.PERSONAL_FAVORITES:
          if (personalFavoriteRef.current) {
            const { favoritesRestaurantItems, foodReviewerItems } =
              personalFavoriteRef.current.getPayload()

            // If both arrays are empty, use SKIP path (no points)
            if (
              (!favoritesRestaurantItems || favoritesRestaurantItems.length === 0) &&
              (!foodReviewerItems || foodReviewerItems.length === 0)
            ) {
              return await proceedToNextStep()
            }

            const updatedPayload = {
              currentStep: currentStep.step,
              nextStep: currentStep?.nextStep as number,
              favoritesRestaurantItems,
              foodReviewerItems,
            }

            setStepsPayloads((prevPayloads) => ({
              ...prevPayloads,
              [currentStep.step]: updatedPayload,
            }))
            payload.favorites = {
              reviewers: foodReviewerItems,
              restaurants: favoritesRestaurantItems,
            }
          }
          break
        case onboardStepTypes.SOCIAL:
          if (onboardSocialRef.current) {
            const socialValue = onboardSocialRef.current.getPayload()
            
            // If all social links are empty/blank, use SKIP path (no points)
            const hasAnySocialLink = !!(
              socialValue?.facebook?.trim() ||
              socialValue?.instagram?.trim() ||
              socialValue?.x?.trim() ||
              socialValue?.tiktok?.trim()
            )
            
            if (!hasAnySocialLink) {
              return await proceedToNextStep()
            }
            
            payload.socialValue = socialValue
          }
          break
        case onboardStepTypes.DINING_FREQUENCY:
          if (onboardDiningFrequencyRef.current) {
            const diningFrequency =
              onboardDiningFrequencyRef.current.getPayload()
            payload.diningFrequency = diningFrequency
          }
          break
        case onboardStepTypes.ADD_SOCIAL_CHANNELS:
          if (socialChannelsRef.current) {
            const socialChannels = socialChannelsRef.current.getPayload()
            payload.socialChannels = socialChannels
          }
          break
        case onboardStepTypes.SELECT_CONTENT_NICHE:
          if (contentNicheRef.current) {
            payload.contentNiche = await contentNicheRef.current.getPayload()
          }
          break
        case onboardStepTypes.DESCRIBE_YOURSELF:
          if (describeYourselfRef.current) {
            payload.describeYourself = describeYourselfRef.current.getPayload()
          }
          break

        default:
          return await proceedToNextStep()
      }

      await handleFetchProfile(payload)
    } else {
      if (currentStep.step === onboardStepTypes.LOCATE_YOUR_RESTAURANT) {
        if (restaurantRef.current) restaurantRef.current.getPayload()
        setActiveStep(currentStep.nextStep as number)
      } else {
        if (onboardingRequestRef.current) {
          try {
            const requestPayload =
              (await onboardingRequestRef.current.getPayload()) as OnboardRequestPayloadType
            if (process.env.REACT_APP_ANALYTICS === 'true') {
              const _tfa = (window as any)._tfa
              _tfa.push({
                notify: 'event',
                name: 'signup',
                id: 1651237,
                revenue: '200',
              })

              const gtag = (window as any).gtag
              gtag('event', 'conversion', {
                send_to: 'AW-11274324379/TpS5COafr8IYEJuTgoAq',
              })
            }

            if (restaurant === undefined) {
              const onboardRequest = {
                name: requestPayload?.restaurantName ?? '',
                contactName: requestPayload?.contactName,
                contactEmail: requestPayload?.contactEmail,
                phone: requestPayload?.phoneNumber,
                website: requestPayload?.websiteLink,
              }
              setLoadingRestaurants(true)
              const resultAction = await dispatch(
                restaurantOnboardRequest(onboardRequest),
              )
              if (restaurantOnboardRequest.fulfilled.match(resultAction)) {
                setLoadingRestaurants(false)
                localStorage.removeItem(PENDING_REFERRAL_CONTEXT_KEY)
                navigate(ROUTES.RESTAURANT_ADMIN_DASHBOARD)
              }
              if (restaurantOnboardRequest.rejected.match(resultAction)) {
                setLoadingRestaurants(false)
              }
            } else {
              const updateRequest = {
                restaurantId: restaurant.restaurantId,
                alias: restaurant.alias,
                name: restaurant.name,
                contactName: requestPayload.contactName,
                contactEmail: requestPayload.contactEmail,
                phone: requestPayload.phoneNumber,
                website: requestPayload.websiteLink,
              }
              setLoadingRestaurants(true)
              const resultAction = await dispatch(
                updateRestaurantOnboard(updateRequest),
              )
              if (updateRestaurantOnboard.fulfilled.match(resultAction)) {
                setLoadingRestaurants(false)
                localStorage.removeItem(PENDING_REFERRAL_CONTEXT_KEY)
                navigate(ROUTES.RESTAURANT_ADMIN_DASHBOARD)
              }
              if (updateRestaurantOnboard.rejected.match(resultAction)) {
                setLoadingRestaurants(false)
                localStorage.removeItem(PENDING_REFERRAL_CONTEXT_KEY)
                navigate(ROUTES.RESTAURANT_ADMIN_DASHBOARD)
              }
            }
          } catch (error) {
            debugLog(error)
          }
        }
      }
    }
  }

  const logoutUser = (): void => {
    analytics?.identify('', { isLoggedIn: false })
    analytics?.reset()
    logoutAuthUser()
    navigate(ROUTES.HOME)
  }

  const setOpenServer = (): void => {
    dispatch(setOpen(false))
  }

  return (
    <OnboardMainContainer>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content="Set up Resactly around your taste. Tell us how you decide, and recommendations get sharper with every choice you make — and you earn for the dining you already do."
        />
      </Helmet>
      <MuiBackdrop
        data-testid="backdropId"
        open={isLoading || isLoadingRestaurants}
      >
        <CircularProgress color="inherit" />
      </MuiBackdrop>
      {!isSmallScreen ? (
        <OnboardSidebarContainer>
          <OnboardSidebarWarper>
            <SideTitleAndStepper>
              <OnboardSidebarLogoImg />
              <OnboardSidebarTitleTypography data-testid="sidebar-title">
                Recommendations that learn from what you actually choose
              </OnboardSidebarTitleTypography>

              <OnboardStepper activeStep={activeStep} steps={steps} />
            </SideTitleAndStepper>

            <OnboardSidebarLogoutButton onClick={logoutUser}>
              <OnboardSidebarButtonTitle>Logout</OnboardSidebarButtonTitle>
              <OnboardSidebarButtonImg />
            </OnboardSidebarLogoutButton>
          </OnboardSidebarWarper>
        </OnboardSidebarContainer>
      ) : (
        <Grid
          item
          xs={12}
          paddingX={2}
          py={2}
          style={{ borderBottom: `1px solid ${theme.palette.grey[200]}` }}
        >
          <div>
            <OnboardStepper activeStep={activeStep} steps={steps} />
          </div>
        </Grid>
      )}
      <OnboardContentContainer>
        <OnboardHeaderWarper
          position={isSmallScreen ? 'absolute' : 'relative'}
          justifyContent={'space-between'}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
            }}
          >
            {activeStep !== onboardStepTypes.CHOOSE_ROLE && (
              <OnboardHeaderBackButton onClick={prevStep}>
                Back
              </OnboardHeaderBackButton>
            )}
            {isSmallScreen && (
              <OnboardHeaderBackButton onClick={logoutUser}>
                Logout
              </OnboardHeaderBackButton>
            )}
          </Box>
          <OnboardHeaderButtonWarper>
            {currentStep.canSkip && (
              <OnboardHeaderSkipButton
                onClick={() => {
                  void handleSkip()
                }}
              >
                Skip
              </OnboardHeaderSkipButton>
            )}
            {selectedRole ? (
              <OnboardHeaderNextButton
                data-testid="next-btn"
                disabled={
                  activeStep === onboardStepTypes.CHOOSE_ROLE
                    ? !selectedRole || !selectedRole.length
                    : isNextButtonDisable
                }
                onClick={() => {
                  void handleNext()
                }}
              >
                {currentStep.nextStep === null ? 'Finish' : 'Next'}
              </OnboardHeaderNextButton>
            ) : (
              <div style={{ height: '34px' }} />
            )}
          </OnboardHeaderButtonWarper>
        </OnboardHeaderWarper>
        <OnboardContent>
          <OnboardContentWarper
            width={role === 'influencer' ? '100%' : 'inherit'}
          >
            {/* Cumulative points banner */}
            {completedRewardedSteps > 0 && (
              <Box
                sx={{
                  mb: 2,
                  position: 'relative',
                  animation: showEarnedAnimation
                    ? `${pulseAnimation} 0.6s ease-in-out`
                    : 'none',
                }}
              >
                <Alert
                  severity="info"
                  sx={{
                    py: 0.5,
                    backgroundColor: showEarnedAnimation
                      ? theme.palette.success.light
                      : theme.palette.info.light,
                    '& .MuiAlert-message': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    },
                  }}
                >
                  <Typography variant="body2">
                    You&apos;ve earned {getOnboardingPointsSoFar(completedRewardedSteps)} points so far
                  </Typography>
                  {showEarnedAnimation && lastEarnedPoints !== null && (
                    <Chip
                      label={`+${lastEarnedPoints} points`}
                      size="small"
                      color="success"
                      sx={{
                        animation: `${fadeOutAnimation} 1.2s ease-out forwards`,
                        ml: 'auto',
                      }}
                    />
                  )}
                </Alert>
              </Box>
            )}
            
            {/* "Earn +5 points" caption for current step */}
            {activeStep !== onboardStepTypes.CHOOSE_ROLE &&
              currentStep.page &&
              getStepPoints(currentStep.page) !== null && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 2 }}
                >
                  Earn +{ONBOARD_STEP_POINTS} points for completing this step
                </Typography>
              )}
            
            {activeStep === onboardStepTypes.CHOOSE_ROLE && (
              <OnboardChooseRole
                validRole={selectedRole}
                setValidRole={setSelectedRole}
                title="How are you using Resactly today?"
                subTitle="Unlock a tailored experience"
                setNextButtonDisable={setNextButtonDisable}
              />
            )}
            {activeStep === onboardStepTypes.PERSONALIZE && (
              <>
                <OnboardPersonalize
                  key={onboardStepTypes.PERSONALIZE}
                  title="Make Resactly yours"
                  subTitle="The more we know about your taste, the sharper every recommendation.
                  What you share stays private — it only shapes the tables you see."
                  setNextButtonDisable={setNextButtonDisable}
                  ref={onboardPersonalizeRef}
                />
              </>
            )}
            {activeStep === onboardStepTypes.INGREDIENTS && (() => {
              const ingredientsPayload = stepsPayloads[onboardStepTypes.INGREDIENTS]
              const likedItems = ingredientsPayload?.likedItems
              const dislikedItems = ingredientsPayload?.dislikedItems
              return (
                <OnboardPreferences
                  ref={itemListRef}
                  key={onboardStepTypes.INGREDIENTS}
                  title="What Ingredients do you like?"
                  subTitle="Likes"
                  secondSubTitle="Dislikes"
                  listName="ingredients"
                  indexKey="REACT_APP_INGREDIENTS_INDEX"
                  page={OnboardPage[currentStep.page]}
                  setNextButtonDisable={setNextButtonDisable}
                  liked={likedItems}
                  dislike={dislikedItems}
                />
              )
            })()}
            {activeStep === onboardStepTypes.CUISINES && (() => {
              const cuisinesPayload = stepsPayloads[onboardStepTypes.CUISINES]
              const likedItems = cuisinesPayload?.likedItems
              const dislikedItems = cuisinesPayload?.dislikedItems
              return (
                <OnboardPreferences
                  ref={itemListRef}
                  key={onboardStepTypes.CUISINES}
                  title="What Cuisines do you like?"
                  subTitle="Likes"
                  secondSubTitle="Dislikes"
                  listName="cuisines"
                  page={OnboardPage[currentStep.page]}
                  setNextButtonDisable={setNextButtonDisable}
                  liked={likedItems}
                  dislike={dislikedItems}
                  indexKey="REACT_APP_CUISINES_INDEX"
                />
              )
            })()}
            {activeStep === onboardStepTypes.FOOD_PREPARATIONS && (() => {
              const preparationsPayload = stepsPayloads[onboardStepTypes.FOOD_PREPARATIONS]
              const likedItems = preparationsPayload?.likedItems
              const dislikedItems = preparationsPayload?.dislikedItems
              return (
                <OnboardPreferences
                  ref={itemListRef}
                  key={onboardStepTypes.FOOD_PREPARATIONS}
                  title="Food Preparations"
                  listName="foodPreparations"
                  subTitle="Likes"
                  secondSubTitle="Dislikes"
                  indexKey="REACT_APP_FOOD_PREPARATION_INDEX"
                  page={OnboardPage[currentStep.page]}
                  setNextButtonDisable={setNextButtonDisable}
                  liked={likedItems}
                  dislike={dislikedItems}
                />
              )
            })()}
            {activeStep === onboardStepTypes.DINING_AMBIENCE && (() => {
              const ambiencePayload = stepsPayloads[onboardStepTypes.DINING_AMBIENCE]
              const likedItems = ambiencePayload?.likedItems
              const dislikedItems = ambiencePayload?.dislikedItems
              return (
                <OnboardPreferences
                  ref={itemListRef}
                  key={onboardStepTypes.DINING_AMBIENCE}
                  title="What Dining Ambience do you like?"
                  subTitle="Likes"
                  secondSubTitle="Dislikes"
                  listName="ambience"
                  page={OnboardPage[currentStep.page]}
                  setNextButtonDisable={setNextButtonDisable}
                  liked={likedItems}
                  dislike={dislikedItems}
                  indexKey="REACT_APP_AMBIENCE_INDEX"
                />
              )
            })()}
            {activeStep === onboardStepTypes.DIETARY_PREFERENCE_RESTRICTION && (() => {
              const dietaryPayload = stepsPayloads[onboardStepTypes.DIETARY_PREFERENCE_RESTRICTION]
              const likedItems = dietaryPayload?.likedItems
              return (
                <OnboardPreferences
                  ref={itemListRef}
                  key={onboardStepTypes.DIETARY_PREFERENCE_RESTRICTION}
                  title="Dietary Preferences and Restrictions"
                  listName="dietaryPreferences"
                  page={OnboardPage[currentStep.page]}
                  setNextButtonDisable={setNextButtonDisable}
                  liked={likedItems}
                  indexKey="REACT_APP_DIETARY_PREFERENCES_INDEX"
                />
              )
            })()}
            {activeStep === onboardStepTypes.ALLERGIES && (() => {
              const allergiesPayload = stepsPayloads[onboardStepTypes.ALLERGIES]
              const likedItems = allergiesPayload?.likedItems
              return (
                <OnboardPreferences
                  ref={itemListRef}
                  key={onboardStepTypes.ALLERGIES}
                  title="What Allergies do you have?"
                  listName="allergies"
                  page={OnboardPage[currentStep.page]}
                  setNextButtonDisable={setNextButtonDisable}
                  liked={likedItems}
                  indexKey="REACT_APP_ALLERGIES_INDEX"
                />
              )
            })()}
            {activeStep === onboardStepTypes.DINING_FREQUENCY && (
              <OnboardDiningFrequency
                ref={onboardDiningFrequencyRef}
                title="Add your dining frequency"
                setNextButtonDisable={setNextButtonDisable}
              />
            )}
            {activeStep === onboardStepTypes.PERSONAL_FAVORITES && (() => {
              const favoritesPayload = stepsPayloads[onboardStepTypes.PERSONAL_FAVORITES]
              const favoritesRestaurant = favoritesPayload?.favoritesRestaurantItems
              const foodReviewers = favoritesPayload?.foodReviewerItems
              return (
                <OnboardPersonalFavorites
                  ref={personalFavoriteRef}
                  key={onboardStepTypes.PERSONAL_FAVORITES}
                  title="Personal Favorites"
                  subTitle="What are your favorite restaurants?"
                  secondSubTitle="What are your favorite reviewers?"
                  firstListName="favoritesRestaurant"
                  secondListName="foodReviewers"
                  setNextButtonDisable={setNextButtonDisable}
                  favoritesRestaurant={favoritesRestaurant}
                  foodReviewers={foodReviewers}
                  firstIndexKey="REACT_APP_RESTAURANT_INDEX"
                  secondIndexKey="REACT_APP_FAVORITE_REVIEWERS_INDEX"
                />
              )
            })()}
            {activeStep === onboardStepTypes.SOCIAL && (
              <OnboardSocial
                ref={onboardSocialRef}
                title="Add your social media links"
                setNextButtonDisable={setNextButtonDisable}
              />
            )}
            {activeStep === onboardStepTypes.LOCATE_YOUR_RESTAURANT && (
              <OnboardLocateYourRestaurant
                ref={restaurantRef}
                title="Locate your Restaurant"
                subTitle="Select your restaurant name below to verify"
                setNextButtonDisable={setNextButtonDisable}
              />
            )}
            {activeStep === onboardStepTypes.REQUEST_ONBOARDING_RESTAURANT && (
              <OnboardRequest
                ref={onboardingRequestRef}
                title={
                  isNewRestaurant ? 'Request Onboarding' : 'Update onboarding'
                }
                subTitle="Please enter your restaurant details"
                setNextButtonDisable={setNextButtonDisable}
              />
            )}
            {activeStep === onboardStepTypes.DESCRIBE_YOURSELF && (
              <OnboardInfluencer title="Describe Yourself and Your Content">
                <DescribeYourselfStep
                  ref={describeYourselfRef}
                  setIsValidData={(isValid) => setNextButtonDisable(!isValid)}
                />
              </OnboardInfluencer>
            )}
            {activeStep === onboardStepTypes.ADD_SOCIAL_CHANNELS && (
              <OnboardInfluencer title="Add Your Social Channels ">
                <SocialChannelsStep
                  ref={socialChannelsRef}
                  setIsValidData={(isValid) => setNextButtonDisable(!isValid)}
                />
              </OnboardInfluencer>
            )}
            {activeStep === onboardStepTypes.SELECT_CONTENT_NICHE && (
              <OnboardInfluencer title="Select the Niche that Match Your Content">
                <ContentNicheStep
                  ref={contentNicheRef}
                  editMode
                  items={onboardContentNicheStepList}
                  setIsValidData={(isValid) => setNextButtonDisable(!isValid)}
                />
              </OnboardInfluencer>
            )}
          </OnboardContentWarper>
        </OnboardContent>
      </OnboardContentContainer>
      <MuiSnackbar open={openServer} onClose={setOpenServer}>
        <MuiAlert onClose={setOpenServer} severity={severity ?? 'success'}>
          {message}
        </MuiAlert>
      </MuiSnackbar>
    </OnboardMainContainer>
  )
}

export default withAuthenticationRequired(Onboard)
